import React, { useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import { offset, flip, shift } from '@floating-ui/dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconName, IconDefinition } from '@fortawesome/fontawesome-svg-core';
import parse from 'html-react-parser';
import PropTypes from 'prop-types';
import { addEvent } from '@utils/tools/GTMUtils';
import {
    GTM_EVENT_CATEGORIES,
    GTM_EVENT_ACTIONS,
} from '@constants/common/gtmEventConstants';

export interface TooltipProps {
    id: string | React.ReactElement;
    content: string | React.ReactNode;
    index?: string;
    elementId?: string;
    href?: string;
    replacement?: Record<string, React.ReactNode>;
    place?: 'left' | 'top' | 'right' | 'bottom';
    type?: 'dark' | 'success' | 'warning' | 'error' | 'info' | 'light';
    icon?: IconName | IconDefinition;
    iconLabel?: string;
    empty?: boolean;
    triggerClassName?: string;
    contentClassName?: string;
    triggerDecorationClassName?: string;
    dataAutomationId?: string;
    plain?: boolean;
    contentReplacement?: any;
    onClickHandler?: (e: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>) => void;
    isIdDynamicText?: boolean;
    linkDomId?: string;
    ariaLabel?: string;
};

/**
 * id - trigger id (used for Formatted message to render trigger text). It is not used for tooltip where trigger is icon;
 * content - content id for Formatted Message or whole html which will be rendered;
 * index - unique identifier to construct unique data-for attributes;
 * replacement - object for trigger value;
 * icon - If this param is passed, tooltip's trigger is not text, but an icon;
 * triggerClassName - custom class names for trigger;
 * contentClassName - custom class names for content;
 * triggerDecorationClassName - by default all tooltips have dotted bottom border;
 * dataAutomationId - optional prop needed for automation QA.
 * plain - when content is a dynamic value, which does not need to be parsed(translated)
 *
 * @param props
 * @returns {*}
 * @constructor
 */
const Tooltip = (props: TooltipProps) => {
    const {
        id,
        elementId=undefined,
        content,
        index,
        href,
        replacement,
        place='top',
        type='dark',
        icon=undefined,
        iconLabel=undefined,
        empty=false,
        triggerClassName='',
        contentClassName='',
        triggerDecorationClassName='tooltip-base',
        dataAutomationId=undefined,
        plain=false,
        contentReplacement,
        onClickHandler,
        isIdDynamicText=false,
        linkDomId = null,
        ariaLabel = undefined
    } = props;

    const intlApi = useIntl();
    const TOOLTIP_DELAY_HIDE = 200;
    const TOOLTIP_DELAY_SHOW = 300;

    const gtmHoverEvent = () => {
        addEvent({
            event: 'hoverEvent',
            eventCategory: GTM_EVENT_CATEGORIES.TOOLTIP,
            eventAction: GTM_EVENT_ACTIONS.HOVER,
            eventLabel: elementId || dataAutomationId || 'Unknown',
            elementId,
            dataAutomationId,
            content,
            componentName: 'Tooltip',
        });
    };

    const uniqueIdentifier = useMemo(() => icon ? `tooltip_${index}` : elementId ? `${elementId}_tooltip_${index}` : `${id}_tooltip_${index}`, [icon, elementId, id, index]);

    const defineLinkPropsIfLink = () => href ? { href, target: '_blank', rel: 'noopener noreferrer' } : { href: '#' };

    const getTooltipTriggerType = () => {
        if (icon) {
            return (
                <>
                    <FontAwesomeIcon icon={icon} />
                    <span className="usa-sr-only">
                        {!!iconLabel && iconLabel} <FormattedMessage id="common.more.info" />
                    </span>
                </>
            ); // icon, with hidden text
        } else if (plain || React.isValidElement(id) || isIdDynamicText) {
            return id; // dynamic text, no need to parse/translate;
        } else if (!empty && typeof id === 'string') {
            return <FormattedMessage id={id} values={replacement}/>; // i18n id, will be translated;
        }
        return null;
    };

    const tooltipTriggerType =  getTooltipTriggerType();

    return <span className='ga-tooltip-container' onMouseOver = { gtmHoverEvent }>
        <a data-tooltip-id={uniqueIdentifier}
            { ...defineLinkPropsIfLink() }
            {...(dataAutomationId ? {'data-automation-id': `${dataAutomationId}_tooltip_base`} : {}) }
            className={`text-base cursor-pointer ${triggerClassName} ${triggerDecorationClassName}`}
            onClick={(e) => { if (!href) e.preventDefault(); onClickHandler?.(e); }}
            {...(linkDomId ? {id: linkDomId} : {}) }
            aria-describedby={uniqueIdentifier}
            aria-label={ariaLabel}
            {...(!href ? { role: 'button' } : {})}
        >
            {tooltipTriggerType}
        </a>
        <span
            className={`gi-tooltip ${contentClassName}`}
            {...(dataAutomationId ? {'data-automation-id': `${dataAutomationId}_tooltip_content`} : {}) }>
            <ReactTooltip
                id={uniqueIdentifier}
                place={place}
                variant={type}
                className='tooltip-component'
                positionStrategy='fixed'
                delayHide={TOOLTIP_DELAY_HIDE}
                delayShow={TOOLTIP_DELAY_SHOW}
                globalCloseEvents={{ escape: true }}
                // Bare `flip()` keeps the tooltip on its `place` axis (opposite-side fallback only),
                // unlike react-tooltip's default which reorients to a perpendicular side.
                middlewares={[offset(10), flip(), shift({ padding: 5 })]}
            >
                { typeof content === 'string' && !plain ? parse(`${intlApi.formatMessage({ id: content }, contentReplacement)}`) : content }
            </ReactTooltip>
        </span>
    </span>;
};

Tooltip.propTypes = {
    id: PropTypes.any.isRequired,
    content: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.array,
        PropTypes.node,
    ]),
    index: PropTypes.string,
    elementId: PropTypes.string,
    href: PropTypes.string,
    replacement: PropTypes.any,
    place: PropTypes.oneOf(['left', 'top', 'right', 'bottom']),
    type: PropTypes.oneOf(['dark', 'success', 'warning', 'error', 'info', 'light']),
    icon: PropTypes.any,
    empty: PropTypes.bool,
    triggerClassName: PropTypes.string,
    contentClassName: PropTypes.string,
    triggerDecorationClassName: PropTypes.string,
    dataAutomationId: PropTypes.string,
    plain: PropTypes.bool,
    contentReplacement: PropTypes.any,
    onClickHandler: PropTypes.func
};

export default Tooltip;